/**
 * Canonical category taxonomy for public documents.
 *
 * Used by:
 * - LLM metadata extraction (cluster-worker) to assign categories
 * - Document repository page (start app) for filtering/grouping
 */
export const CATEGORY_GROUPS: Record<string, readonly string[]> = {
  'Health and Medicine': [
    'General Medicine',
    'Public Health',
    'Global Health',
    'Epidemiology',
    'Internal Medicine',
    'Neurology',
    'Oncology',
    'Pediatrics',
    'Cardiology',
    'Mental Health',
    'Infectious Diseases',
    'Virology',
    'Nutrition',
    'Healthcare Management',
    'Obstetrics and Gynecology',
    'Medical Research',
    'Health Policy',
    'Health Informatics',
    'Health Sociology',
    'Health Economics',
    'Biostatistics',
    'Bioinformatics',
    'Epigenetics',
    'COVID-19 Research',
  ],
  'Biology and Natural Sciences': [
    'Biology',
    'Natural Sciences',
    'Genetics',
    'Microbiology',
    'Ecology',
    'Evolutionary Biology',
    'Neuroscience',
    'Cognitive Science',
    'Biodiversity',
    'Conservation Biology',
    'Science Metrics',
    'Science Communication',
  ],
  Psychology: [
    'Clinical Psychology',
    'Cognitive Psychology',
    'Social Psychology',
    'Behavioral Psychology',
    'Developmental Psychology',
    'Health Psychology',
    'Organizational Psychology',
  ],
  'Social Sciences': [
    'Sociology',
    'Economics',
    'Political Science',
    'Anthropology',
    'Gender Studies',
    'Linguistics',
    'Sociolinguistics',
    'Social Theory',
    'Cultural Sociology',
    'Sociology of Education',
  ],
  'Environmental Studies': [
    'Climate Science',
    'Climate Change',
    'Sustainability',
  ],
  Humanities: [
    'History',
    'Ancient History',
    'Philosophy',
    'Ethics',
    'Literature',
    'Religion and Spirituality',
    'Arts and Culture',
  ],
  'Technology and Engineering': [
    'Computer Science',
    'Artificial Intelligence',
    'Software Engineering',
    'Data Science',
    'Information Technology',
    'Information Science',
    'Cybersecurity',
  ],
  'Business and Finance': [
    'Management',
    'Marketing',
    'Entrepreneurship',
    'Accounting',
    'Investment',
    'Corporate Strategy',
  ],
  'Law and Legal Studies': [
    'Constitutional Law',
    'Criminal Law',
    'International Law',
    'Intellectual Property',
    'Contracts and Regulation',
  ],
  Education: [
    'Pedagogy',
    'Higher Education',
    'Online Learning',
    'Curriculum Design',
  ],
  'Mathematics and Statistics': [
    'Mathematics',
    'Applied Mathematics',
    'Statistics',
  ],
  'Physical Sciences': ['Physics', 'Chemistry', 'Astronomy', 'Earth Sciences'],
  'Communication and Media': ['Journalism', 'Media Studies'],
  'Self-Help and Personal Development': [
    'Productivity',
    'Wellness',
    'Motivation',
  ],
  'Fiction and Creative Writing': ['Novel', 'Short Story', 'Poetry', 'Drama'],
  'Government and Policy': [
    'Public Policy',
    'International Relations',
    'Public Administration',
  ],
  'Interdisciplinary Studies': [],
  Other: [],
};

export const PUBLIC_DOC_MAIN_CATEGORIES = Object.keys(CATEGORY_GROUPS);

// NOTE: do NOT re-export `PUBLIC_DOC_CATEGORY_SLUGS` from here. This module is
// imported via a raw `import()` in the build-time sitemap hook
// (`apps/start/vite.config.ts`'s `contentSlugsFn`), which resolves through the
// build runtime — not vite's bundler — and that resolver can't follow a bare
// extensionless relative import. The barrel (`api-contract.ts`) and the
// dedicated `./public-doc-category-slugs` subpath both expose the slugs
// directly, so nothing needs the hop through this file.

/** Public docs expose one canonical main category. */
export const DOCUMENT_CATEGORIES = PUBLIC_DOC_MAIN_CATEGORIES;

export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number];

/** Map lowercase category name → canonical casing. */
export const DOCUMENT_CATEGORY_MAP: ReadonlyMap<string, string> = new Map(
  DOCUMENT_CATEGORIES.map((c) => [c.toLowerCase(), c]),
);

/** Reverse lookup: subcategory → parent group name. */
const _subcategoryToGroup = new Map<string, string>();
for (const [group, subs] of Object.entries(CATEGORY_GROUPS)) {
  for (const sub of subs) {
    _subcategoryToGroup.set(sub, group);
  }
}

/**
 * Given a comma-separated category string (e.g. "Epidemiology, Health and Medicine, Public Health"),
 * return the primary group name. Checks each tag against group names first, then subcategory→group map.
 */
export function resolvePublicDocCategory(categoryStr: string | null): string {
  if (!categoryStr) return 'Other';
  const tags = categoryStr.split(',').map((t) => t.trim());
  // Prefer a tag that is itself a group name
  for (const tag of tags) {
    if (tag in CATEGORY_GROUPS) return tag;
  }
  // Fall back to subcategory→group lookup
  for (const tag of tags) {
    const group = _subcategoryToGroup.get(tag);
    if (group) return group;
  }
  return 'Other';
}
